import { useEffect, useRef, useState } from "react";
import { Smartphone, Loader2 } from "lucide-react";
import { PhoneMultiFactorGenerator } from "firebase/auth";
import { makeRecaptcha, sendSignInCode, finishSignIn, maskPhone, mfaMessage } from "../../auth/mfa";

/**
 * The second step of a sign-in (or a "confirm it's you" check) for an account with SMS
 * two-step sign-in: pick the phone, send the code, enter it.
 * @param resolver  from the auth/multi-factor-auth-required error
 * @param onDone    called with the completed UserCredential
 */
export default function SmsCodeStep({ resolver, onDone, onCancel, accent = "bg-teal-600 hover:bg-teal-700" }) {
    const phones = resolver.hints.filter(h => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
    const [hint, setHint] = useState(phones[0] || null);
    const [verificationId, setVerificationId] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const containerId = useRef(`recaptcha-${Math.random().toString(36).slice(2)}`).current;
    const verifier = useRef(null);
    useEffect(() => () => verifier.current?.clear(), []);

    const send = async () => {
        setBusy(true); setError("");
        try {
            verifier.current?.clear();
            verifier.current = makeRecaptcha(containerId);
            setVerificationId(await sendSignInCode(resolver, hint, verifier.current));
            setCode("");
        } catch (err) {
            console.warn("Two-step sign-in:", err.code, err.message); setError(mfaMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const verify = async e => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            onDone(await finishSignIn(resolver, verificationId, code.trim()));
        } catch (err) {
            console.warn("Two-step sign-in:", err.code, err.message); setError(mfaMessage(err));
            setBusy(false);
        }
    };

    if (!phones.length) return <p className="text-sm text-red-600 font-semibold">This account uses a second step this app doesn't support yet. Contact support.</p>;

    return (
        <div className="space-y-4">
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center shrink-0"><Smartphone size={18} /></div>
                <div>
                    <p className="font-bold text-slate-800">Two-step sign-in</p>
                    <p className="text-sm text-slate-500">We'll text a 6-digit code to your phone.</p>
                </div>
            </div>
            {error && <p className="text-sm text-red-600 font-semibold">{error}</p>}

            {phones.length > 1 && !verificationId && (
                <div className="space-y-1.5">
                    {phones.map(p => (
                        <label key={p.uid} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                            <input type="radio" name="mfa-phone" checked={hint?.uid === p.uid} onChange={() => setHint(p)} />
                            {p.displayName ? `${p.displayName} · ` : ""}{maskPhone(p.phoneNumber)}
                        </label>
                    ))}
                </div>
            )}

            {!verificationId ? (
                <button type="button" onClick={send} disabled={busy} className={`w-full text-white p-3 rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2 ${accent}`}>
                    {busy && <Loader2 size={16} className="animate-spin" />} Text a code to {maskPhone(hint?.phoneNumber)}
                </button>
            ) : (
                <form onSubmit={verify} className="space-y-3">
                    <p className="text-sm text-slate-500">Code sent to {maskPhone(hint?.phoneNumber)}.</p>
                    <input
                        autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6-digit code"
                        className="w-full p-3 border border-gray-300 rounded-md tracking-[0.4em] text-center text-lg"
                        value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ""))} required
                    />
                    <button type="submit" disabled={busy || code.length !== 6} className={`w-full text-white p-3 rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2 ${accent}`}>
                        {busy && <Loader2 size={16} className="animate-spin" />} Verify
                    </button>
                    <button type="button" onClick={send} disabled={busy} className="w-full text-sm text-slate-500 hover:text-slate-800 disabled:opacity-50">Send a new code</button>
                </form>
            )}
            {onCancel && <button type="button" onClick={onCancel} className="w-full text-sm text-slate-400 hover:text-slate-700">Cancel</button>}
            <div id={containerId} />
        </div>
    );
}
